import React from "react";
import { Typography, Grid, Box, Divider, Stack, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import Controls from "../atoms/Controls";
import ValidationTooltip from "../atoms/validationToolTip";

interface ContactInfo {
  firstNames?: string;
  lastName?: string;
  email?: string;
  salutation?: string;
  businessName?: string | null;
  addressLine1?: string;
  addressLine2?: string;
  phone1?: string;
}

interface Doctor {
  id?: string;
  LANR?: string | null;
  contactInfo?: ContactInfo;
}

interface Clinic {
  id?: string;
  BSNR?: string | null;
  contactInfo?: ContactInfo;
}

interface ValidationData {
  signatureValidation?: {
    validity?: string;
  };
}

interface DoctorCardProps {
  doctor?: Doctor | any;
  clinic?: Clinic | any;
  formData?: {
    doctorNumber?: string | null;
    signatureStatus?: string;
    clinic?: Clinic | any;
  };
  validationData?: ValidationData;
  showSignatureCheck?: boolean;
  readOnlyView?: boolean;
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Called after a successful doctor/clinic ID migration so the parent can refresh */
  onMigrationComplete?: (type: "doctor" | "clinic", newId: string) => void;
  /** Called when user clicks the edit (pencil) icon to open doctor/clinic modal */
  onEdit?: () => void;
  /** Called when user clicks the X next to the doctor section to unlink the doctor from the Rx */
  onRemoveDoctor?: () => void;
  /** Called when user clicks the X next to the clinic section to unlink the clinic from the Rx */
  onRemoveClinic?: () => void;
  sx?: object;
}

const DoctorCard: React.FC<DoctorCardProps> = ({
  doctor,
  clinic,
  formData,
  validationData,
  showSignatureCheck = false,
  readOnlyView = false,
  onInputChange,
  onMigrationComplete,
  onEdit,
  onRemoveDoctor,
  onRemoveClinic,
  sx = {},
}) => {
  const currentDoctor = doctor || formData?.doctorNumber;
  const currentClinic = clinic || formData?.clinic;

  return (
    <Box
      sx={{ 
        paddingTop: "5px", 
        marginBottom: "8px", 
        textAlign: "left",
        ...sx 
      }}
    >
      {/* Edit action button */}
      {!readOnlyView && onEdit && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
          <IconButton
            size="small"
            onClick={onEdit}
            title="Arzt & Praxis bearbeiten"
            sx={{ color: "primary.dark" }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {/* Combined Doctor and Clinic Information */}
      <Box mb={showSignatureCheck ? 2 : 0}>
        <Stack spacing={2}>
          {/* Doctor Information */}
          {currentDoctor && (currentDoctor.contactInfo || currentDoctor.LANR) ? (
            <Box sx={{ position: "relative", pr: !readOnlyView && onRemoveDoctor ? 4 : 0 }}>
              <Typography variant="body2" sx={{ fontSize: "0.9rem", fontWeight: "600", mb: 1 }}>
                {currentDoctor.contactInfo
                  ? currentDoctor.contactInfo.lastName
                    ? `${currentDoctor.contactInfo.lastName}, ${currentDoctor.contactInfo.firstNames || ""} ${currentDoctor.contactInfo.salutation || "Dr."}`
                    : "unbekannt"
                  : currentDoctor.LANR
                    ? `Arzt (${currentDoctor.LANR})`
                    : "unbekannt"
                }
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                LANR: {currentDoctor?.LANR || ""}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                Tel: {currentDoctor?.contactInfo?.phone1 || ""}
              </Typography>
              {!readOnlyView && onRemoveDoctor && (
                <IconButton
                  size="small"
                  onClick={onRemoveDoctor}
                  title="Arzt vom Rezept entfernen"
                  sx={{ position: "absolute", top: -4, right: -4, color: "error.main" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontSize: "0.8rem", fontStyle: "italic", color: "text.secondary" }}>
              Kein Arzt angegeben
            </Typography>
          )}

          {/* Clinic Information */}
          {currentClinic && (currentClinic.contactInfo || currentClinic.BSNR) ? (
            <Box sx={{ position: "relative", pr: !readOnlyView && onRemoveClinic ? 4 : 0 }}>
              <Typography variant="body2" sx={{ fontSize: "0.85rem", fontWeight: "500", mb: 0.5 }}>
                {currentClinic.contactInfo?.businessName || "Praxis"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary", mb: 1 }}>
                BSNR: {currentClinic.BSNR || ""}
              </Typography>
              {currentClinic.contactInfo?.email && (
                <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary", mb: 1 }}>
                  {currentClinic.contactInfo.email}
                </Typography>
              )}
              <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                {currentClinic.contactInfo?.addressLine1 || ""}
                {currentClinic.contactInfo?.addressLine2 && (
                  <>
                    <br />
                    {currentClinic.contactInfo.addressLine2}
                  </>
                )}
                {currentClinic.contactInfo?.phone1 && (
                  <>
                    <br />
                    Tel: {currentClinic.contactInfo.phone1}
                  </>
                )}
              </Typography>
              {!readOnlyView && onRemoveClinic && (
                <IconButton
                  size="small"
                  onClick={onRemoveClinic}
                  title="Praxis vom Rezept entfernen"
                  sx={{ position: "absolute", top: -4, right: -4, color: "error.main" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontSize: "0.8rem", fontStyle: "italic", color: "text.secondary" }}>
              Keine Praxis angegeben
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Optional Signature Check Section */}
      {showSignatureCheck && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ValidationTooltip
                  validity={validationData?.signatureValidation?.validity}
                  message={validationData?.signatureValidation?.validity || ""}
                  placement="left"
                  sx={{ zIndex: 1300 }}
                >
                  <Controls.RadioGroup
                    label={
                      <Typography sx={{ textAlign: "left", fontWeight: "bold" }}>
                        Arztunterschrift vorhanden
                      </Typography>
                    }
                    name="signatureStatus"
                    value={formData?.signatureStatus}
                    options={[
                      { id: "nicht kontrolliert", label: "nicht kontrolliert" },
                      { id: "vorhanden", label: "vorhanden" },
                      { id: "fehlend", label: "fehlend" },
                    ]}
                    disabled={readOnlyView}
                    onChange={onInputChange}
                    sx={{
                      width: "100%",
                      paddingLeft: "1rem",
                      textAlign: "left",
                      fontSize: "0.7rem",
                    }}
                  />
                </ValidationTooltip>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DoctorCard;

